import { useState, useEffect, useCallback } from "react";
import StatCard from "./components/StatCard";
import QuickAction from "./components/QuickAction";
import { fetchOdalar, fetchRezervasyonlar, fetchMusteriler, fetchKaynaklar, formatWhatsappLink, type Rezervasyon, type Musteri, type Kaynak } from "@/services/api";

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rezervasyonlar, setRezervasyonlar] = useState<Rezervasyon[]>([]);
  const [odalar, setOdalar] = useState<{ id: number; durum: string; aktif: boolean }[]>([]);
  const [musteriler, setMusteriler] = useState<Musteri[]>([]);
  const [kaynaklar, setKaynaklar] = useState<Kaynak[]>([]);
  const [otelTel, setOtelTel] = useState("05395851199");

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [rezData, odaData, musData, kayData] = await Promise.all([
        fetchRezervasyonlar(),
        fetchOdalar(),
        fetchMusteriler(),
        fetchKaynaklar(),
      ]);
      setRezervasyonlar(rezData);
      setOdalar(odaData.filter(o => o.aktif));
      setMusteriler(musData);
      setKaynaklar(kayData);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Veri yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const today = new Date().toISOString().split("T")[0];

  const todayCheckins = rezervasyonlar.filter(r => r.giris_tarihi === today);
  const todayCheckouts = rezervasyonlar.filter(r => r.cikis_tarihi === today);
  const stayingGuests = rezervasyonlar.filter(r => r.durum === "giris_yapildi" || r.durum === "konakliyor");
  const emptyRooms = odalar.filter(r => r.durum === "bos");
  const occupiedRooms = odalar.filter(r => r.durum === "dolu" || r.durum === "rezerve");

  const pendingReservations = rezervasyonlar.filter(r => r.durum === "teklif_verildi" || r.durum === "kapora_bekleniyor");
  const depositPending = rezervasyonlar.filter(r => r.durum === "kapora_bekleniyor");

  const monthlyRevenue = rezervasyonlar
    .filter(r => r.durum === "konakliyor" || r.durum === "rezervasyon_onaylandi" || r.durum === "giris_yapildi")
    .reduce((sum, r) => sum + Number(r.toplam_tutar || 0), 0);

  const todayCollection = rezervasyonlar
    .filter(r => r.giris_tarihi === today)
    .reduce((sum, r) => sum + Number(r.alinan_kapora || 0), 0);

  const activeRooms = odalar.filter(r => r.aktif).length;
  const occupancyRate = activeRooms > 0 ? Math.round((occupiedRooms.length / activeRooms) * 100) : 0;

  const adSourceIds = kaynaklar
    .filter(k => k.ad.includes("Instagram") || k.ad.includes("Facebook"))
    .map(k => k.id);
  const adLeads = musteriler.filter(m => m.kaynak_id && adSourceIds.includes(m.kaynak_id));
  const convertedAdLeads = adLeads.filter(m => m.durum === "konakliyor" || m.durum === "rezervasyon_yapti");

  const followUps = rezervasyonlar.filter(r =>
    r.durum === "teklif_verildi" || r.durum === "kapora_bekleniyor" || r.odeme_durumu === "odeme_alinmadi"
  );

  const getKaynakAd = (kaynakId: number | undefined) => kaynaklar.find(k => k.id === kaynakId)?.ad || "-";

  const getStatusBadge = (durum: string) => {
    const m: Record<string, string> = {
      "konakliyor": "bg-accent-100 text-accent-700",
      "giris_yapildi": "bg-accent-100 text-accent-700",
      "rezervasyon_onaylandi": "bg-primary-100 text-primary-700",
      "kapora_bekleniyor": "bg-secondary-300 text-secondary-800",
      "teklif_verildi": "bg-secondary-100 text-secondary-700",
    };
    return m[durum] || "bg-background-200 text-foreground-600";
  };

  const getPaymentBadge = (durum: string) => {
    const m: Record<string, string> = {
      "tamami_odendi": "bg-accent-100 text-accent-700",
      "kapora_alinmadi": "bg-primary-100 text-primary-700",
      "kismi_odeme": "bg-secondary-200 text-secondary-800",
      "odeme_alinmadi": "bg-red-100 text-red-600",
    };
    return m[durum] || "bg-background-200 text-foreground-600";
  };

  const durumLabel = (d: string) => {
    const m: Record<string, string> = {
      "konakliyor": "Konaklıyor", "giris_yapildi": "Giriş Yapıldı", "rezervasyon_onaylandi": "Rezervasyon Onaylandı",
      "kapora_bekleniyor": "Kapora Bekleniyor", "teklif_verildi": "Teklif Verildi", "iptal_edildi": "İptal Edildi",
      "no_show": "No Show", "cikis_yapildi": "Çıkış Yapıldı",
    };
    return m[d] || d;
  };

  const odemeLabel = (d: string) => {
    const m: Record<string, string> = {
      "tamami_odendi": "Tamamı Ödendi", "kapora_alindi": "Kapora Alındı",
      "kismi_odeme": "Kısmi Ödeme", "odeme_alinmadi": "Ödeme Alınmadı",
    };
    return m[d] || d;
  };

  const cleanPhone = (tel: string) => tel ? tel.replace(/[\s()\-]/g, "").replace(/^0/, "90") : "";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-foreground-500">Veriler yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4 text-center">
          <span className="w-12 h-12 flex items-center justify-center rounded-full bg-red-50">
            <i className="ri-error-warning-line text-red-500 text-xl"></i>
          </span>
          <p className="text-sm text-foreground-600">{error}</p>
          <button onClick={loadData} className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 cursor-pointer whitespace-nowrap">
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-foreground-900 font-heading">Genel Bakış</h1>
          <p className="text-sm text-foreground-500 mt-0.5">
            Bugünkü girişler, çıkışlar, doluluk ve tahsilat özetinizi buradan takip edebilirsiniz
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm"></i></span>
            Yeni Rezervasyon
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        <StatCard label="Bugünkü Girişler" value={todayCheckins.length} icon="ri-login-box-line" colorClass="text-primary-600" bgClass="bg-primary-100" />
        <StatCard label="Bugünkü Çıkışlar" value={todayCheckouts.length} icon="ri-logout-box-line" colorClass="text-accent-600" bgClass="bg-accent-100" />
        <StatCard label="Konaklayan Misafir" value={stayingGuests.length} icon="ri-user-smile-line" colorClass="text-primary-600" bgClass="bg-primary-100/70" />
        <StatCard label="Boş Oda" value={emptyRooms.length} icon="ri-hotel-bed-line" colorClass="text-accent-600" bgClass="bg-accent-100/70" />
        <StatCard label="Dolu Oda" value={occupiedRooms.length} icon="ri-hotel-line" colorClass="text-secondary-700" bgClass="bg-secondary-200" />
        <StatCard label="Doluluk Oranı" value={`%${occupancyRate}`} icon="ri-pie-chart-line" colorClass="text-primary-600" bgClass="bg-primary-100" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Bekleyen Rezervasyon" value={pendingReservations.length} icon="ri-time-line" colorClass="text-secondary-700" bgClass="bg-secondary-200" />
        <StatCard label="Kapora Bekleyen" value={depositPending.length} icon="ri-bank-card-line" colorClass="text-red-500" bgClass="bg-red-50" />
        <StatCard label="Bugünkü Tahsilat" value={`₺${todayCollection.toLocaleString("tr-TR")}`} icon="ri-money-dollar-circle-line" colorClass="text-accent-600" bgClass="bg-accent-100" />
        <StatCard label="Bu Ay Gelir" value={`₺${monthlyRevenue.toLocaleString("tr-TR")}`} icon="ri-funds-line" colorClass="text-primary-600" bgClass="bg-primary-100" />
        <StatCard label="Reklam Lead" value={adLeads.length} icon="ri-advertisement-line" colorClass="text-secondary-700" bgClass="bg-secondary-200" />
        <StatCard label="Rezervasyona Dönen" value={convertedAdLeads.length} icon="ri-check-double-line" colorClass="text-accent-600" bgClass="bg-accent-100" />
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h3 className="text-sm font-medium text-foreground-700 mb-3">Hızlı İşlemler</h3>
        <div className="flex flex-wrap gap-2">
          <QuickAction label="Yeni Rezervasyon" icon="ri-add-circle-line" colorClass="bg-primary-500 text-white hover:bg-primary-600" />
          <QuickAction label="Yeni Müşteri" icon="ri-user-add-line" colorClass="bg-accent-500 text-white hover:bg-accent-600" />
          <QuickAction label="Hızlı Teklif" icon="ri-file-text-line" colorClass="bg-secondary-500 text-white hover:bg-secondary-600" />
          <QuickAction label="Ödeme Ekle" icon="ri-money-dollar-circle-line" colorClass="bg-primary-100 text-primary-700 hover:bg-primary-200" />
          <QuickAction label="Giriş Yapıldı" icon="ri-login-box-line" colorClass="bg-accent-100 text-accent-700 hover:bg-accent-200" />
          <QuickAction label="Çıkış Yapıldı" icon="ri-logout-box-line" colorClass="bg-secondary-200 text-secondary-800 hover:bg-secondary-300" />
          <QuickAction label="WhatsApp Mesajı" icon="ri-whatsapp-line" colorClass="bg-green-100 text-green-700 hover:bg-green-200" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-background-200/70 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground-800 font-heading flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-login-box-line text-primary-500 text-sm"></i></span>
              Bugün Giriş Yapacaklar
            </h3>
            <span className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded-full">{todayCheckins.length} kişi</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200/70 bg-background-100/50">
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Müşteri</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Telefon</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Oda</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Kişi</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Ödeme</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Kaynak</th>
                  <th className="text-center px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {todayCheckins.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-8 text-foreground-400 text-sm">Bugün giriş yapacak misafir bulunmuyor</td></tr>
                ) : todayCheckins.map(r => {
                  const musteri = musteriler.find(m => m.id === r.musteri_id);
                  return (
                    <tr key={r.id} className="border-b border-background-100 hover:bg-background-100/50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap"><span className="font-medium text-foreground-800 text-sm">{musteri?.ad_soyad || "-"}</span></td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-foreground-600">{musteri?.telefon || "-"}</td>
                      <td className="px-4 py-3 whitespace-nowrap"><span className="text-xs text-foreground-700">{r.oda_tipi}</span></td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-foreground-600">{r.kisi_sayisi}</td>
                      <td className="px-4 py-3 whitespace-nowrap"><span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${getPaymentBadge(r.odeme_durumu)}`}>{odemeLabel(r.odeme_durumu)}</span></td>
                      <td className="px-4 py-3 whitespace-nowrap"><span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">{getKaynakAd(r.kaynak_id)}</span></td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center justify-center gap-1">
                          <a href={formatWhatsappLink(musteri?.telefon || otelTel, "")} target="_blank" rel="nofollow noopener noreferrer" className="w-7 h-7 rounded-md bg-green-100 text-green-600 flex items-center justify-center hover:bg-green-200 transition-colors cursor-pointer" title="WhatsApp">
                            <i className="ri-whatsapp-line text-xs"></i>
                          </a>
                          <button className="w-7 h-7 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center hover:bg-primary-200 transition-colors cursor-pointer" title="Giriş Yapıldı">
                            <i className="ri-check-line text-xs"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-background-200/70 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground-800 font-heading flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-logout-box-line text-accent-500 text-sm"></i></span>
              Bugün Çıkış Yapacaklar
            </h3>
            <span className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded-full">{todayCheckouts.length} kişi</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200/70 bg-background-100/50">
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Müşteri</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Oda No</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Çıkış</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Kalan</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Not</th>
                  <th className="text-center px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {todayCheckouts.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-8 text-foreground-400 text-sm">Bugün çıkış yapacak misafir bulunmuyor</td></tr>
                ) : todayCheckouts.map(r => {
                  const musteri = musteriler.find(m => m.id === r.musteri_id);
                  const oda = odalar.find(o => o.id === r.oda_id);
                  return (
                    <tr key={r.id} className="border-b border-background-100 hover:bg-background-100/50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap"><span className="font-medium text-foreground-800 text-sm">{musteri?.ad_soyad || "-"}</span></td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-foreground-700">{oda ? `Oda ${oda.id}` : "-"}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-foreground-600">{r.cikis_saati || "11:00"}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{Number(r.kalan_tutar) > 0 ? <span className="text-xs font-medium text-red-600">₺{Number(r.kalan_tutar).toLocaleString("tr-TR")}</span> : <span className="text-xs text-accent-600">-</span>}</td>
                      <td className="px-4 py-3 max-w-[140px]"><p className="text-xs text-foreground-500 truncate">{r.notlar || "-"}</p></td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center justify-center gap-1">
                          <a href={formatWhatsappLink(musteri?.telefon || otelTel, "")} target="_blank" rel="nofollow noopener noreferrer" className="w-7 h-7 rounded-md bg-green-100 text-green-600 flex items-center justify-center hover:bg-green-200 transition-colors cursor-pointer" title="WhatsApp">
                            <i className="ri-whatsapp-line text-xs"></i>
                          </a>
                          <button className="w-7 h-7 rounded-md bg-accent-100 text-accent-600 flex items-center justify-center hover:bg-accent-200 transition-colors cursor-pointer" title="Çıkış Yapıldı">
                            <i className="ri-check-double-line text-xs"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/70 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground-800 font-heading flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-user-follow-line text-secondary-600 text-sm"></i></span>
            Takip Edilecek Müşteriler
          </h3>
          <span className="text-xs text-foreground-500 bg-secondary-100 px-2 py-0.5 rounded-full">{followUps.length} kişi</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200/70 bg-background-100/50">
                <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Müşteri</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Telefon</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Tarih Aralığı</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Oda Tercihi</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Kaynak</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Durum</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">Son Not</th>
                <th className="text-center px-4 py-2.5 text-xs font-medium text-foreground-500 whitespace-nowrap">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {followUps.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-8 text-foreground-400 text-sm">Takip edilecek müşteri bulunmuyor</td></tr>
              ) : followUps.map(r => {
                const musteri = musteriler.find(m => m.id === r.musteri_id);
                return (
                  <tr key={r.id} className="border-b border-background-100 hover:bg-background-100/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap"><span className="font-medium text-foreground-800 text-sm">{musteri?.ad_soyad || "-"}</span></td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-foreground-600">{musteri?.telefon || "-"}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-foreground-600">{r.giris_tarihi} - {r.cikis_tarihi}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-foreground-700">{r.oda_tipi}</td>
                    <td className="px-4 py-3 whitespace-nowrap"><span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">{getKaynakAd(r.kaynak_id)}</span></td>
                    <td className="px-4 py-3 whitespace-nowrap"><span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${getStatusBadge(r.durum)}`}>{durumLabel(r.durum)}</span></td>
                    <td className="px-4 py-3 max-w-[160px]"><p className="text-xs text-foreground-500 truncate">{r.notlar || "-"}</p></td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-1">
                        <a href={formatWhatsappLink(musteri?.telefon || otelTel, "")} target="_blank" rel="nofollow noopener noreferrer" className="w-7 h-7 rounded-md bg-green-100 text-green-600 flex items-center justify-center hover:bg-green-200 transition-colors cursor-pointer" title="WhatsApp">
                          <i className="ri-whatsapp-line text-xs"></i>
                        </a>
                        <button className="w-7 h-7 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center hover:bg-secondary-200 transition-colors cursor-pointer" title="Takip Et">
                          <i className="ri-user-follow-line text-xs"></i>
                        </button>
                        <button className="w-7 h-7 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center hover:bg-primary-200 transition-colors cursor-pointer" title="Rezervasyona Çevir">
                          <i className="ri-arrow-right-circle-line text-xs"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}